import React from 'react';
import { Phone, ArrowRight, Activity, MapPin } from 'lucide-react';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useTenantTheme } from '../TenantThemeContext';
import {
    AmbientBlob,
    GridBackdrop,
    MagneticButton,
    RevealOnScroll
} from '../ui/primitives';
import HeroBackdrop from '../ui/HeroBackdrop';
import SectionParticles from '../ui/SectionParticles';

function HeroCtas({ phone, slugBase, tenant }) {
    const { language } = useLanguage();
    const lat = tenant?.latitude;
    const lng = tenant?.longitude;
    const address = tenant?.address || tenant?.location;
    const directionsUrl = (lat && lng)
        ? `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`
        : address
            ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`
            : null;

    return (
        <div className="mt-10 flex flex-wrap items-center gap-3">
            {phone && (
                <MagneticButton
                    as="a"
                    href={`tel:${phone}`}
                    size="lg"
                    icon={<Phone className="w-4 h-4" />}
                    className="normal-case tracking-normal"
                >
                    <span className="font-semibold">{phone}</span>
                </MagneticButton>
            )}
            <MagneticButton
                as="a"
                href={`${slugBase}#tracker`}
                variant="secondary"
                size="lg"
                icon={<Activity className="w-4 h-4" />}
            >
                {language === 'bn' ? 'জব ট্র্যাক' : 'Track My Job'}
            </MagneticButton>
            {directionsUrl && (
                <MagneticButton
                    as="a"
                    href={directionsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="ghost"
                    size="lg"
                    icon={<MapPin className="w-4 h-4" />}
                >
                    {language === 'bn' ? 'রাস্তা দেখুন' : 'Get Directions'}
                </MagneticButton>
            )}
        </div>
    );
}

function HeroEyebrow({ tenant }) {
    const { language } = useLanguage();
    if (!tenant?.address) return null;
    return (
        <div className="inline-flex items-center gap-2 text-sm md:text-[15px] font-semibold uppercase tracking-[0.22em] text-[var(--tenant-primary)] mb-6">
            <MapPin className="w-4 h-4" />
            <span className={language === 'bn' && tenant.address_bn ? 'font-bn normal-case tracking-normal text-base font-semibold' : ''}>
                {language === 'bn' && tenant.address_bn ? tenant.address_bn : tenant.address}
            </span>
        </div>
    );
}

function HeadingPair({ tenant }) {
    const { language } = useLanguage();
    const tagline = (language === 'bn' && tenant.tagline_bn) ? tenant.tagline_bn : tenant.tagline;
    return (
        <>
            <h1 className="font-headline font-extrabold text-[12vw] md:text-[6.5vw] lg:text-[5.25rem] leading-[0.92] tracking-[-0.02em] text-[var(--tenant-ink)]">
                {tenant.name}
            </h1>
            {tenant.name_bn && language === 'bn' && (
                <div className="font-bn text-3xl md:text-5xl mt-2 text-[var(--tenant-ink)]">
                    {tenant.name_bn}
                </div>
            )}
            {tagline && (
                <p className="mt-6 text-lg md:text-xl text-[var(--tenant-muted)] max-w-xl leading-relaxed">
                    {tagline}
                </p>
            )}
        </>
    );
}

// ---- Variant: Forest (default — cinematic full-bleed cover + dust) ----
function HeroForest({ tenant, slugBase }) {
    return (
        <section className="relative overflow-hidden" id="hero">
            {/* Full-bleed cover + tint */}
            <div className="absolute inset-0 z-0">
                <HeroBackdrop coverUrl={tenant.cover_image_url} name={tenant.name} fullBleed />
            </div>
            {/* Theme-green dust on top of the tint */}
            <SectionParticles density={48} speed={1.6} className="z-[2]" />
            <AmbientBlob className="-top-20 -left-20 z-[1]" size={520} />
            <AmbientBlob className="bottom-[-200px] right-[-100px] z-[1]" size={620} style={{ background: 'var(--tenant-blob-b)' }} delay={0.4} />
            <GridBackdrop opacity={0.05} />
            <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-8 pt-16 md:pt-24 pb-24 md:pb-36">
                <RevealOnScroll className="max-w-3xl">
                    <HeroEyebrow tenant={tenant} />
                    <HeadingPair tenant={tenant} />
                    <HeroCtas phone={tenant.phone} slugBase={slugBase} tenant={tenant} />
                </RevealOnScroll>
            </div>
        </section>
    );
}

// ---- Variant: Workshop (industrial — big numerals & stamped feel) ----
function HeroWorkshop({ tenant, slugBase }) {
    return (
        <section className="relative overflow-hidden" id="hero">
            <div className="absolute inset-0 z-0">
                <HeroBackdrop coverUrl={tenant.cover_image_url} name={tenant.name} fullBleed />
            </div>
            <SectionParticles density={42} speed={1.5} className="z-[2]" />
            <GridBackdrop opacity={0.08} />
            <AmbientBlob className="top-10 right-[-80px] z-[1]" size={460} style={{ background: 'var(--tenant-blob-b)' }} />
            <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-8 pt-16 md:pt-24 pb-24 md:pb-32">
                <RevealOnScroll className="relative z-10 max-w-4xl">
                    <HeroEyebrow tenant={tenant} />
                    <HeadingPair tenant={tenant} />
                    <HeroCtas phone={tenant.phone} slugBase={slugBase} tenant={tenant} />
                </RevealOnScroll>
            </div>
        </section>
    );
}

// ---- Variant: Showroom (cinematic image + minimal text) ----
function HeroShowroom({ tenant, slugBase }) {
    return (
        <section className="relative overflow-hidden" id="hero">
            {tenant.cover_image_url ? (
                <div className="absolute inset-0">
                    <img
                        src={tenant.cover_image_url}
                        alt=""
                        className="w-full h-full object-cover"
                        loading="eager"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[var(--tenant-surface)] via-[var(--tenant-surface)]/50 to-transparent" />
                </div>
            ) : (
                <AmbientBlob className="top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" size={780} />
            )}
            <div className="relative max-w-7xl mx-auto px-5 md:px-8 pt-32 md:pt-48 pb-16 md:pb-32">
                <RevealOnScroll className="max-w-3xl">
                    <HeroEyebrow tenant={tenant} />
                    <HeadingPair tenant={tenant} />
                    <HeroCtas phone={tenant.phone} slugBase={slugBase} tenant={tenant} />
                </RevealOnScroll>
            </div>
        </section>
    );
}

// ---- Variant: Express (energetic + ticker) ----
function HeroExpress({ tenant, slugBase }) {
    const { language } = useLanguage();
    const ticker = language === 'bn'
        ? ['৩০ মিনিটে সার্ভিস', 'এক্সপ্রেস লেন', 'অরিজিনাল পার্টস', 'লাইভ ট্র্যাকিং']
        : ['30-min service', 'Express lane', 'Authentic parts', 'Live tracking'];
    return (
        <section className="relative overflow-hidden" id="hero">
            <div className="absolute inset-0 z-0">
                <HeroBackdrop coverUrl={tenant.cover_image_url} name={tenant.name} fullBleed />
            </div>
            <SectionParticles density={52} speed={1.8} className="z-[2]" />
            <AmbientBlob className="-top-10 -left-10 z-[1]" size={500} />
            <AmbientBlob className="bottom-0 right-0 z-[1]" size={620} style={{ background: 'var(--tenant-blob-b)' }} delay={0.3} />
            <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-8 pt-16 md:pt-24 pb-16 md:pb-24">
                <RevealOnScroll className="max-w-3xl">
                    <HeroEyebrow tenant={tenant} />
                    <HeadingPair tenant={tenant} />
                    <HeroCtas phone={tenant.phone} slugBase={slugBase} tenant={tenant} />
                </RevealOnScroll>
            </div>
            {/* Marquee ticker */}
            <div className="relative z-10 border-y border-[var(--tenant-border)] bg-[var(--tenant-surface)]/80 backdrop-blur-sm overflow-hidden">
                <div className="flex gap-12 py-4 whitespace-nowrap font-display tracking-[0.18em] text-2xl text-[var(--tenant-primary)] animate-marquee motion-reduce:animate-none">
                    {[...ticker, ...ticker, ...ticker].map((t, i) => (
                        <span key={i} className="flex items-center gap-12">
                            {t}
                            <span className="inline-block w-2 h-2 rounded-full bg-[var(--tenant-accent)]" />
                        </span>
                    ))}
                </div>
            </div>
        </section>
    );
}

// ---- Variant: Heritage (warm, testimonial-led) ----
function HeroHeritage({ tenant, slugBase }) {
    const { language } = useLanguage();
    return (
        <section className="relative overflow-hidden" id="hero">
            <GridBackdrop opacity={0.05} />
            <div className="relative max-w-7xl mx-auto px-5 md:px-8 pt-14 md:pt-24 pb-16 md:pb-28 grid lg:grid-cols-[1fr_1fr] gap-12 items-center">
                <RevealOnScroll>
                    <div className="text-[11px] uppercase tracking-[0.34em] text-[var(--tenant-primary)] mb-6">
                        {language === 'bn' ? 'ঐতিহ্যের যত্ন' : 'A trusted name since day one'}
                    </div>
                    <HeadingPair tenant={tenant} />
                    <HeroCtas phone={tenant.phone} slugBase={slugBase} tenant={tenant} />
                </RevealOnScroll>
                {tenant.cover_image_url ? (
                    <RevealOnScroll className="relative">
                        <div className="aspect-[4/5] rounded-[2rem] overflow-hidden border border-[var(--tenant-border)]">
                            <img src={tenant.cover_image_url} alt="" className="w-full h-full object-cover" />
                        </div>
                        <div className="absolute -bottom-6 -left-6 px-5 py-3 bg-[var(--tenant-primary)] text-[var(--tenant-surface)] rounded-full font-display text-2xl tracking-wider">
                            {tenant.name}
                        </div>
                    </RevealOnScroll>
                ) : (
                    <div className="relative h-[420px] hidden lg:block">
                        <AmbientBlob className="top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" size={520} />
                    </div>
                )}
            </div>
        </section>
    );
}

const VARIANTS = {
    forest: HeroForest,
    workshop: HeroWorkshop,
    showroom: HeroShowroom,
    express: HeroExpress,
    heritage: HeroHeritage
};

export default function Hero({ slugBase }) {
    const { tenant, template } = useTenantTheme();
    if (!tenant) return null;
    const variant = template?.heroVariant || 'forest';
    const Variant = VARIANTS[variant] || HeroForest;
    return <Variant tenant={tenant} slugBase={slugBase} />;
}
